/**
 * UsageExhaustedModal — RC 1.33 baseline, RC 1.34 plan-aware copy,
 * RC 1.41.G4.F numeric breakdown.
 *
 * Shown when the backend returns 429 USAGE_EXHAUSTED after the user
 * tries to run an action that draws on their usage buckets, or when
 * the wizard's client-side pre-flight check
 * (`ensureCreditsAvailable`) blocks the action before submission.
 * Copy switches on `planCode`:
 *
 *   - base (Base user whose daily free limit is exhausted and whose
 *     purchased extra usage is also insufficient)
 *   - focus / flow (included + extra usage exhausted)
 *
 * RC 1.41.G4.F additions (all optional, backward-compatible):
 *   • `onClose` — alias for `onOpenChange(false)`. Wizard call sites
 *     prefer the explicit close handler.
 *   • `required`, `availableIncluded`, `availableExtra`,
 *     `action` — surface a small "Credits required: X · Available: Y"
 *     breakdown line when supplied. Omitted gives identical pre-G4.F
 *     copy.
 *
 * CTA is "Add Credits" → `/account?tab=plan`, which is where the
 * RC 1.34 Add-usage stub flow lives. Candidate-facing copy uses
 * "Credits" exclusively (RC 1.41.G4.A+ Credits language sweep).
 */
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, ArrowLeft } from 'lucide-react';

const ACTION_LABELS = {
  analyze_fit:      'Analyse Fit',
  cv_improve:       'Generate CV',
  cover_letter:     'Generate cover letter',
  application_pack: 'Create application pack',
};

export default function UsageExhaustedModal({
  open,
  onOpenChange,
  onClose,
  planCode = 'focus',
  action = null,
  required = null,
  availableIncluded = null,
  availableExtra = null,
}) {
  const navigate = useNavigate();
  const handleOpenChange = (v) => {
    if (typeof onOpenChange === 'function') onOpenChange(v);
    if (!v && typeof onClose === 'function') onClose();
  };

  const isBase = planCode === 'base';
  const title = isBase
    ? 'Daily free limit reached'
    : 'Credits exhausted';
  const body = isBase
    ? 'You’ve used your free analyses for today. Add extra Credits to continue now, or come back tomorrow.'
    : 'You’ve used your available Credits. Add extra Credits to continue.';

  const totalAvailable =
    (Number.isFinite(Number(availableIncluded)) ? Number(availableIncluded) : 0) +
    (Number.isFinite(Number(availableExtra))    ? Number(availableExtra)    : 0);
  const showBreakdown =
    Number.isFinite(Number(required)) &&
    (availableIncluded != null || availableExtra != null);
  const actionLabel = ACTION_LABELS[action] || null;

  const goBackToJobs = () => {
    handleOpenChange(false);
    navigate('/jobs');
  };

  const goAddUsage = () => {
    handleOpenChange(false);
    navigate('/account?tab=plan');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[460px]"
        data-testid="usage-exhausted-modal"
        data-plan-code={planCode}
        data-action={action || undefined}
      >
        <DialogHeader>
          <DialogTitle data-testid="usage-exhausted-modal-title">
            {title}
          </DialogTitle>
          <DialogDescription
            className="text-sm text-ndor-ink-secondary leading-relaxed"
            data-testid="usage-exhausted-modal-body"
          >
            {body}
          </DialogDescription>
        </DialogHeader>
        {showBreakdown && (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 text-xs text-ndor-ink-secondary leading-relaxed space-y-0.5"
            data-testid="usage-exhausted-modal-breakdown"
          >
            {actionLabel && (
              <p>
                <span className="text-ndor-ink-muted">Action:</span>{' '}
                <span className="text-ndor-ink">{actionLabel}</span>
              </p>
            )}
            <p>
              <span className="text-ndor-ink-muted">Credits required:</span>{' '}
              <span className="font-mono-ui tabular-nums text-ndor-ink">{Number(required)}</span>
            </p>
            <p>
              <span className="text-ndor-ink-muted">Credits available:</span>{' '}
              <span className="font-mono-ui tabular-nums text-ndor-ink">
                {totalAvailable}
              </span>
              {(availableIncluded != null || availableExtra != null) && (
                <span className="text-ndor-ink-muted">
                  {' '}({Number(availableIncluded) || 0} included · {Number(availableExtra) || 0} extra)
                </span>
              )}
            </p>
          </div>
        )}
        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={goBackToJobs}
            data-testid="usage-exhausted-modal-back-to-jobs"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to jobs
          </Button>
          <Button
            onClick={goAddUsage}
            data-testid="usage-exhausted-modal-add-usage"
            className="bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Credits
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
